import { useState, useEffect, useMemo } from 'react';
import { bookService } from 'services/bookService';
import { bookRecordService } from 'services/bookRecordService';
import { shelfService } from 'services/shelfService';
import type { Book, BookRecord, Shelf } from 'types';
import { BookCard } from 'components/public/BookCard';
import { Layout } from 'components/common/Layout';
import { searchBook } from 'utils/search';
import './HomePage.css';

interface BookWithStatus extends Book {
  isAvailable: boolean;
  borrowerRecord?: BookRecord;
  shelf_number?: string | null;
}

export const HomePage = () => {
  const [allBooks, setAllBooks] = useState<BookWithStatus[]>([]);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const booksPerPage = 20;

  const shelfById = useMemo(() => {
    const map = new Map<number, Shelf>();
    shelves.forEach((shelf) => map.set(shelf.id, shelf));
    return map;
  }, [shelves]);

  const filteredBooks = allBooks
    .filter((book) => {
      // Multi-way search: title, author, borrower name
      const borrowerName = book.borrowerRecord?.borrower_name;
      const matchesSearch = searchBook(searchTerm, book, borrowerName);
      const matchesFilter = !activeFilter ||
        (activeFilter === 'available' && book.isAvailable) ||
        (activeFilter === 'borrowed' && !book.isAvailable);
      const matchesCategory = !categoryFilter ||
        (book.category && book.category.split(',').map(cat => cat.trim()).some(cat =>
          cat.toLowerCase() === categoryFilter.toLowerCase()
        ));
      return matchesSearch && matchesFilter && matchesCategory;
    })
    // Newest added first (higher id / later added_date)
    .sort((a, b) => {
      const dateDiff = new Date(b.added_date).getTime() - new Date(a.added_date).getTime();
      if (dateDiff !== 0) return dateDiff;
      return b.id - a.id;
    });

  const totalPages = Math.ceil(filteredBooks.length / booksPerPage);
  const displayedBooks = filteredBooks.slice((currentPage - 1) * booksPerPage, currentPage * booksPerPage);

  useEffect(() => {
    loadAllBooks();
  }, []);

  useEffect(() => {
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [searchTerm, activeFilter, categoryFilter]);

  const loadAllBooks = async () => {
    try {
      setIsLoading(true);

      const [allBooksList, borrowedRecords, shelvesList] = await Promise.all([
        bookService.getAllBooks({ limit: 1000 }),
        bookRecordService.getAllRecords({ status: 'borrowed', limit: 1000 }),
        shelfService.getAllShelves({ limit: 100 }).catch(() => [] as Shelf[]),
      ]);

      const borrowedMap = new Map(borrowedRecords.map(record => [record.book_id, record]));
      const shelfMap = new Map(shelvesList.map(shelf => [shelf.id, shelf]));

      const booksWithStatus: BookWithStatus[] = allBooksList.map(book => ({
        ...book,
        isAvailable: !borrowedMap.has(book.id),
        borrowerRecord: borrowedMap.get(book.id),
        shelf_number: book.shelf_id != null ? (shelfMap.get(book.shelf_id)?.shelf_number ?? null) : null,
      }));

      setAllBooks(booksWithStatus);
      setShelves(shelvesList);
    } catch (err) {
      console.error('Error loading books:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const handlePageClick = (page: number) => {
    setCurrentPage(page);
  };

  // Generate page numbers to display
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      // Show all pages if total pages <= 5
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);
      
      if (currentPage <= 3) {
        // Near the start
        for (let i = 2; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end (loop already includes totalPages)
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Extract unique categories from books (split comma-separated values)
  const uniqueCategories = Array.from(
    new Set(
      allBooks
        .flatMap(book => (book.category ? book.category.split(',').map(cat => cat.trim()) : []))
        .filter(cat => cat.length > 0)
        .map(cat => cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase())
    )
  ).sort();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  if (isLoading) {
    return (
      <div className="loading-container" style={{ minHeight: '60vh' }}>
        <div className="spinner"></div>
        <p>Loading books...</p>
      </div>
    );
  }

  return (
    <Layout>
      <div className="home-page">
        {/* Hero Section */}
        <div className="hero-section">
          <div className="hero-overlay"></div>
          <div className="hero-content">
            <h1 className="hero-title">Discover Your Next Adventure</h1>
            <p className="hero-subtitle">
              Explore our vast collection of books, journals, and digital resources.
            </p>

            {/* Search Bar */}
            <div className="hero-search">
              <div className="search-input-wrapper">
                <span className="material-symbols-outlined">search</span>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by title, author, or borrower name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button className="btn btn-primary search-btn">
                Search
              </button>
            </div>
          </div>
        </div>

      {/* Category Filters */}
      <div className="category-filters">
        <button
          className={`filter-chip ${activeFilter === '' ? 'active' : ''}`}
          onClick={() => setActiveFilter('')}
        >
          All
        </button>
        <button
          className={`filter-chip ${activeFilter === 'available' ? 'active' : ''}`}
          onClick={() => setActiveFilter('available')}
        >
          Available
        </button>
        <button
          className={`filter-chip ${activeFilter === 'borrowed' ? 'active' : ''}`}
          onClick={() => setActiveFilter('borrowed')}
        >
          Borrowed
        </button>
        <div className="category-select-wrapper">
          <select
            className="category-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All Categories</option>
            {uniqueCategories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
        {categoryFilter && (
          <button
            className="category-clear-btn"
            onClick={() => setCategoryFilter('')}
            title="Clear category filter"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        )}
      </div>

      {/* Books Grid */}
      <div className="books-section">
        <div className="section-header">
          <h2>New Arrivals</h2>
        </div>

        {/* Total Count - Constant library statistics */}
        <div className="total-count total-count-top">
          <strong>Total:</strong> {allBooks.length} book{allBooks.length !== 1 ? 's' : ''} |
          <span className="available-count"> {allBooks.filter(b => b.isAvailable).length} Available</span> |
          <span className="borrowed-count"> {allBooks.filter(b => !b.isAvailable).length} Borrowed</span>
        </div>

        {displayedBooks.length === 0 && filteredBooks.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📖</div>
            <p className="empty-state-text">
              {searchTerm || activeFilter || categoryFilter
                ? 'No books match your search'
                : 'No books in the library'}
            </p>
          </div>
        ) : (
          <div className="books-grid">
            {displayedBooks.map((book) => (
              <BookCard
                key={book.id}
                book={{
                  ...book,
                  shelf_number:
                    book.shelf_number ??
                    (book.shelf_id != null ? shelfById.get(book.shelf_id)?.shelf_number : null),
                }}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination-container">
            <button
              className="btn btn-secondary pagination-btn"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <div className="pagination-numbers">
              {getPageNumbers().map((page, index) =>
                page === '...' ? (
                  <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                    ...
                  </span>
                ) : (
                  <button
                    key={page}
                    className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageClick(page as number)}
                  >
                    {page}
                  </button>
                )
              )}
            </div>
            <button
              className="btn btn-secondary pagination-btn"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        )}
      </div>
      </div>
    </Layout>
  );
};
